interface RadiusProps {
  topLeftRadius: number;
  topRightRadius: number;
  bottomLeftRadius: number;
  bottomRightRadius: number;
}

export type AnimationDirection =
  | { type: 'horizontal' }
  | { type: 'vertical'; fromY: number | undefined };

interface GetRectanglePathProps extends RadiusProps {
  x: number;
  y: number;
  width: number;
  height: number;
}

export const getRectanglePath = ({
  x,
  y,
  width,
  height,
  topLeftRadius,
  topRightRadius,
  bottomLeftRadius,
  bottomRightRadius,
}: GetRectanglePathProps) => {
  return `
    M${x + topLeftRadius},${y} 
    h${width - topRightRadius - topLeftRadius} 
    q${topRightRadius}, 0, ${topRightRadius}, ${topRightRadius} 
    v${height - bottomRightRadius - topRightRadius} 
    q0, ${bottomRightRadius}, ${-bottomRightRadius} ${bottomRightRadius}  
    h${-(width - bottomRightRadius - bottomLeftRadius)}
    q${-bottomLeftRadius}, 0, ${-bottomLeftRadius}, ${-bottomLeftRadius}
    v${-(height - bottomLeftRadius - topLeftRadius)}
    q0, ${-topLeftRadius}, ${topLeftRadius} ${-topLeftRadius} 
    Z`;
};

const getMinPossibleHeight = ({
  topLeftRadius,
  topRightRadius,
  bottomLeftRadius,
  bottomRightRadius,
}: RadiusProps) => {
  const totalVerticalRadiusLeft = topLeftRadius + bottomLeftRadius;
  const totalVerticalRadiusRight = topRightRadius + bottomRightRadius;
  const maxTotalVerticalRadius = Math.max(
    totalVerticalRadiusLeft,
    totalVerticalRadiusRight,
  );

  return maxTotalVerticalRadius;
};

export const getMinPossibleWidth = ({
  topLeftRadius,
  topRightRadius,
  bottomLeftRadius,
  bottomRightRadius,
}: RadiusProps) => {
  const totalHorizontalRadiusTop = topLeftRadius + topRightRadius;
  const totalHorizontalRadiusBottom = bottomLeftRadius + bottomRightRadius;
  const maxTotalHorizontalRadius = Math.max(
    totalHorizontalRadiusTop,
    totalHorizontalRadiusBottom,
  );

  return maxTotalHorizontalRadius;
};

export const getStartingAnimationPath = ({
  x,
  y,
  width,
  height,
  topLeftRadius,
  topRightRadius,
  bottomLeftRadius,
  bottomRightRadius,
  animationDirection,
}: GetRectanglePathProps & { animationDirection: AnimationDirection }) => {
  if (animationDirection.type === 'vertical') {
    if (animationDirection.fromY) {
      return getRectanglePath({
        x,
        y: animationDirection.fromY,
        width,
        height,
        topLeftRadius,
        topRightRadius,
        bottomLeftRadius,
        bottomRightRadius,
      });
    }

    // animate from lowest possible height
    const minPossibleHeight = getMinPossibleHeight({
      topLeftRadius,
      topRightRadius,
      bottomLeftRadius,
      bottomRightRadius,
    });
    const initialY = y + height - minPossibleHeight;

    return getRectanglePath({
      x,
      y: initialY,
      width,
      height: minPossibleHeight,
      topLeftRadius,
      topRightRadius,
      bottomLeftRadius,
      bottomRightRadius,
    });
  }

  // animate horizontally from lowest possible width
  const minPossibleWidth = getMinPossibleWidth({
    topLeftRadius,
    topRightRadius,
    bottomLeftRadius,
    bottomRightRadius,
  });

  return getRectanglePath({
    x,
    y,
    width: minPossibleWidth,
    height,
    topLeftRadius,
    topRightRadius,
    bottomLeftRadius,
    bottomRightRadius,
  });
};
